import { useRoute } from '#imports'
import { type ComputedRef, type MaybeRefOrGetter, computed, toValue } from 'vue'
import type { LocationQuery, RouteLocationRaw, RouteParams } from 'vue-router'

import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/app/composables/config/useBuybackConfig'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { ROUTE_NAMES } from '~/scopes/buyback/constants'

import { useBuybackFunnelOffer } from './useBuybackFunnelOffer'

export type UseBuybackFunnelStepsOptions = {
  /**
   * Funnel route params (slug, id, listingId) to build the next steps with.
   * Defaults to the current route params, override it when called outside of the funnel.
   */
  params?: MaybeRefOrGetter<RouteParams>
  /**
   * Query forwarded to the next steps. Defaults to the current route query.
   */
  query?: MaybeRefOrGetter<LocationQuery>
}

export function useBuybackFunnelSteps(
  options: UseBuybackFunnelStepsOptions = {},
) {
  const route = useRoute()
  const experiments = useExperiments()
  const buybackConfig = useBuybackConfig()
  const { offer } = useBuybackFunnelOffer()

  const params = computed<RouteParams>(() =>
    options.params === undefined ? route.params : toValue(options.params),
  )
  const query = computed<LocationQuery>(() =>
    options.query === undefined ? route.query : toValue(options.query),
  )

  const hasBankStep: ComputedRef<boolean> = computed(
    () =>
      Boolean(offer.value?.bankDetailsRequired) &&
      buybackConfig.hasBankStep &&
      experiments['experiment.resaleBankStep'] !== 'withoutBankStep',
  )

  const isSmartphoneCategory: ComputedRef<boolean> = computed(() => {
    const categoryId = params.value.id

    return !Array.isArray(categoryId) && categoryId === 'smartphone'
  })

  const hasImeiStep: ComputedRef<boolean> = computed(
    () =>
      isSmartphoneCategory.value &&
      (experiments['experiment.resaleImeiStepV2'] === 'withMandatoryImei' ||
        experiments['experiment.resaleImeiStepV2'] === 'withOptionalImei'),
  )

  const isImeiMandatory: ComputedRef<boolean> = computed(
    () => experiments['experiment.resaleImeiStepV2'] === 'withMandatoryImei',
  )

  const nextStepAfterImei: ComputedRef<RouteLocationRaw> = computed(() => {
    return hasBankStep.value
      ? {
          name: ROUTE_NAMES.BANK,
          params: { ...params.value },
          query: { ...query.value },
        }
      : {
          name: ROUTE_NAMES.ADDRESS,
          params: { ...params.value },
          query: { ...query.value },
        }
  })

  const nextStepAfterOffer: ComputedRef<RouteLocationRaw> = computed(() => {
    if (hasImeiStep.value) {
      return {
        name: ROUTE_NAMES.IMEI,
        params: { ...params.value },
        query: { ...query.value },
      }
    }

    return nextStepAfterImei.value
  })

  return {
    hasBankStep,
    hasImeiStep,
    isImeiMandatory,
    nextStepAfterOffer,
    nextStepAfterImei,
  }
}
